import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import {
  CreditCard,
  Megaphone,
  Gamepad2,
  Radio,
  ShoppingCart,
  Building2,
  GraduationCap,
  Plane,
  Server,
  Globe2,
  Award,
  type LucideIcon,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";


type Industry = {
  title: string;
  icon: LucideIcon;
  gradient: string;
  line1: string;
  line2: string;
};

const industries: Industry[] = [
  { title: "Fintech & Payments", icon: CreditCard, gradient: "icon-tile-teal", line1: "Secure OTP delivery", line2: "Transaction alerts" },
  { title: "Digital Marketing Agencies", icon: Megaphone, gradient: "icon-tile-indigo", line1: "Bulk campaign delivery", line2: "Customer engagement tools" },
  { title: "iGaming", icon: Gamepad2, gradient: "icon-tile-blue", line1: "Instant verification messages", line2: "High-volume SMS routing" },
  { title: "Telecom & CPaaS", icon: Radio, gradient: "icon-tile-orange", line1: "Carrier-grade messaging", line2: "Global voice connectivity" },
  { title: "Retail & Ecomerce", icon: ShoppingCart, gradient: "icon-tile-pink", line1: "Promotional SMS campaigns", line2: "Order & delivery updates" },
  { title: "Real Estate", icon: Building2, gradient: "icon-tile-green", line1: "Lead notification systems", line2: "Client communication flows" },
  { title: "Education Marketing", icon: GraduationCap, gradient: "icon-tile-teal", line1: "Student engagement messaging", line2: "Application status alerts" },
  { title: "Travel & Transportation", icon: Plane, gradient: "icon-tile-indigo", line1: "Booking confirmation SMS", line2: "Real-time travel updates" },
];

const faqs = [
  { question: "Which industries use business SMS messaging most?", answer: "Fintech, retail, iGaming, telecom, digital marketing, real estate, education, and travel industries rely on SMS for OTP delivery, order alerts, promotions, and real-time customer communication." },
  { question: "How does Yootelco support fintech and payment companies specifically?", answer: "Yootelco delivers OTP codes, transaction alerts, and fraud notifications in under 5 seconds through SOC 2 compliant, carrier-grade infrastructure built for financial-grade reliability." },
  { question: "Can Yootelco handle high-volume SMS for iGaming platforms?", answer: "Yes. Yootelco handles millions of verification messages daily with 99.99% uptime, priority routing for time-sensitive OTP delivery, and grey route elimination to protect platform integrity." },
  { question: "What SMS solutions does Yootelco offer for retail and e-commerce businesses?", answer: "Yootelco supports promotional SMS campaigns, order confirmations, shipping updates, and delivery notifications with direct carrier coverage across 200+ countries." },
  { question: "How does Yootelco help telecom operators and CPaaS providers?", answer: "Yootelco provides carrier-grade A2P SMS routing, global voice termination, and direct Tier-1 interconnects that telecom operators and CPaaS platforms rely on for high-volume traffic." },
  { question: "Does Yootelco support messaging for education and travel companies?", answer: "Yes. Yootelco delivers student engagement messages, application status alerts, booking confirmations, and real-time travel updates reliably across all regions and time zones." },
  { question: "How does Yootelco serve digital marketing agencies?", answer: "Yootelco provides bulk SMS delivery with high throughput, real-time campaign analytics, sender ID registration support, and white-route only policy for maximum deliverability and compliance." },
  { question: "What makes Yootelco different from other SMS providers for enterprise industries?", answer: "Yootelco combines 18+ years of telecom expertise, direct Tier-1 carrier connections, 99.99% uptime SLA, and industry-specific compliance support, making it the trusted choice for enterprises that cannot afford delivery failures." },
];

const stats = [
  { value: "8+", label: "Industries Served" },
  { value: "Global", label: "Coverage" },
  { value: "99.99%", label: "Delivery Uptime" },
  { value: "18+", label: "Years of Expertise" },
];

const whyChoose = [
  { icon: Server, title: "Enterprise-Grade Infrastructure", description: "Carrier-level reliability built for high-demand industries that cannot afford downtime.", gradient: "icon-tile-teal" },
  { icon: Globe2, title: "Global Coverage", description: "Direct carrier connections across 200+ countries ensure your messages reach customers anywhere.", gradient: "icon-tile-indigo" },
  { icon: Award, title: "Industry Expertise", description: "18+ years of telecom experience means we understand the unique challenges of your sector.", gradient: "icon-tile-blue" },
];

const Industries = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const metaTitle = "Industries We Serve | Telecom Solutions | Yootelco";
  const metaDescription =
    "Yootelco delivers carrier-grade SMS, OTP and voice solutions for fintech, banking, telecom, retail, iGaming, healthcare and more.";

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <main className="min-h-screen bg-white">
      <Seo title={metaTitle} description={metaDescription} path="/industries" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[900px] text-center">
            <h1 className="font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px]">
              Industries We Serve
            </h1>
            <p className="mx-auto mt-[18px] max-w-[680px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px]">
              From fintech to iGaming, we deliver the messaging infrastructure that high-demand industries depend on every day.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Industries</h2>
          <p className="mx-auto mt-4 max-w-[640px] text-[15px] leading-[1.6] text-muted-foreground">
            Messaging and voice solutions tailored for high-demand industries where delivery, speed, and reliability matter.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {industries.slice(0, 6).map((ind) => {
            const Icon = ind.icon;
            return (
              <article
                key={ind.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${ind.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{ind.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">
                  {ind.line1}
                  <br />
                  {ind.line2}
                </p>
              </article>
            );
          })}
        </div>
        {/* Last row centered */}
        <div className="mx-auto mt-5 grid max-w-[740px] grid-cols-1 gap-5 sm:grid-cols-2">
          {industries.slice(6).map((ind) => {
            const Icon = ind.icon;
            return (
              <article
                key={ind.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${ind.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{ind.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">
                  {ind.line1}
                  <br />
                  {ind.line2}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Stats Bar */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[1200px] rounded-[24px] bg-[#0f1623] px-6 py-12 md:px-12 md:py-14">
          <div className="grid grid-cols-2 gap-y-10 md:grid-cols-4 md:gap-0">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`text-center ${i > 0 ? "md:border-l md:border-white/15" : ""}`}
              >
                <div className="font-display text-[36px] font-bold leading-none text-white md:text-[44px]">
                  {s.value}
                </div>
                <div className="mt-3 text-[13px] font-medium uppercase tracking-wider text-white/70 md:text-[13.5px]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Why Businesses Choose Yootelco</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 md:grid-cols-3">
          {whyChoose.map((w) => {
            const Icon = w.icon;
            return (
              <article
                key={w.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${w.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{w.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{w.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
            <CTASection
        title={<><span className="text-sky">Ready</span> to get started?</>}
        description="Find the right messaging solution for your industry."
        useCase="Industries"
      />

      <SiteFooter />
    </main>
  );
};

export default Industries;